import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, Clock, Loader2, AlertTriangle, Server, User, CreditCard, HardDrive, Globe, FileText, Mail, ExternalLink, Copy, Database, ChevronDown, ChevronRight, Code, Bug, SkipForward } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminOrderDetail() {
  var params = useParams();
  var navigate = useNavigate();
  var auth = useAdminAuth();
  
  var stateLoading = React.useState(true);
  var stateData = React.useState(null);
  var stateRetrying = React.useState(null);
  var stateScapiLoading = React.useState(false);
  var stateScapiData = React.useState(null);
  var stateScapiOpen = React.useState(false);
  // Track which steps have expanded debug sections
  var stateExpandedSteps = React.useState({});
  
  function fetchData() {
    fetch(API_URL + '/api/admin/orders/' + params.orderId, {
      headers: { Authorization: 'Bearer ' + auth.token }
    })
    .then(function(r) { return r.json(); })
    .then(function(d) { stateData[1](d); stateLoading[1](false); })
    .catch(function() { toast.error('Laden fehlgeschlagen'); stateLoading[1](false); });
  }
  
  function handleRetry(stepKey) {
    stateRetrying[1](stepKey);
    fetch(API_URL + '/api/admin/orders/' + params.orderId + '/retry-step/' + stepKey, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + auth.token }
    })
    .then(function(r) { 
      if (!r.ok) throw new Error(); 
      toast.success('Wird wiederholt'); 
      setTimeout(fetchData, 1000); 
    })
    .catch(function() { toast.error('Retry fehlgeschlagen'); })
    .finally(function() { stateRetrying[1](null); });
  }
  
  var stateSkipping = React.useState(null);
  
  function handleSkip(stepKey) {
    if (!window.confirm('Schritt "' + stepKey + '" wirklich überspringen? Der nächste Schritt wird automatisch gestartet.')) return;
    stateSkipping[1](stepKey);
    fetch(API_URL + '/api/admin/orders/' + params.orderId + '/skip-step/' + stepKey, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + auth.token }
    })
    .then(function(r) { 
      if (!r.ok) return r.json().then(function(e) { throw new Error(e.detail || 'Fehler'); });
      return r.json();
    })
    .then(function(d) { 
      toast.success(d.message || 'Schritt übersprungen'); 
      setTimeout(fetchData, 1000); 
    })
    .catch(function(e) { toast.error(e.message || 'Skip fehlgeschlagen'); })
    .finally(function() { stateSkipping[1](null); });
  }
  
  function fetchScapiStatus() {
    stateScapiLoading[1](true);
    stateScapiData[1](null);
    fetch(API_URL + '/api/admin/orders/' + params.orderId + '/scapi-status', {
      headers: { Authorization: 'Bearer ' + auth.token }
    })
    .then(function(r) { 
      if (!r.ok) return r.json().then(function(e) { throw new Error(e.detail || 'Fehler'); });
      return r.json(); 
    })
    .then(function(d) { stateScapiData[1](d); })
    .catch(function(e) { toast.error(e.message); stateScapiData[1]({ error: e.message }); })
    .finally(function() { stateScapiLoading[1](false); });
  }
  
  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    toast.success('Kopiert!');
  }
  
  function toggleStepExpanded(stepKey) {
    stateExpandedSteps[1](function(prev) {
      var newState = Object.assign({}, prev);
      newState[stepKey] = !prev[stepKey];
      return newState;
    });
  }
  
  React.useEffect(function() {
    fetchData();
  }, [params.orderId]);
  
  // Fetch SCAPI status when modal opens
  React.useEffect(function() {
    if (stateScapiOpen[0]) {
      fetchScapiStatus();
    }
  }, [stateScapiOpen[0]]);
  
  if (stateLoading[0]) {
    return React.createElement('div', { className: 'flex justify-center p-12' },
      React.createElement(Loader2, { className: 'h-8 w-8 animate-spin' })
    );
  }
  
  var order = stateData[0] && stateData[0].order;
  var customer = stateData[0] && stateData[0].customer;
  var plan = (stateData[0] && stateData[0].plan) || (order && order.plan) || {};
  var instance = stateData[0] && stateData[0].instance;
  
  if (!order) {
    return React.createElement('div', { className: 'text-center py-12' },
      React.createElement('p', { className: 'text-muted-foreground mb-4' }, 'Bestellung nicht gefunden'),
      React.createElement(Button, { onClick: function() { navigate('/admin/orders'); } }, 'Zurück')
    );
  }
  
  var steps = order.provisioning_steps || [];
  
  function renderStep(step) {
    var icons = {
      contact_create: User,
      domain_registration: Globe,
      domain_job_wait: Clock,  // NEW: Waiting for job confirmation
      dns_setup: Server,
      dns_zone: Server,  // Legacy support
      dns_verification: RefreshCw,
      nextcloud_create: HardDrive,
      nextcloud_ready: CheckCircle2,
      invoice_create: FileText,
      email_welcome: Mail
    };
    var Icon = icons[step.key] || Server;
    var canRetry = step.status === 'failed' || step.status === 'skipped';
    var canSkip = step.status === 'pending' || step.status === 'running' || step.status === 'failed';
    var borderClass = step.status === 'failed' ? 'border-red-500/50 bg-red-900/20' : step.status === 'success' ? 'border-emerald-500/30 bg-emerald-900/20' : step.status === 'skipped' ? 'border-amber-500/30 bg-amber-900/20' : 'border-slate-700 bg-slate-800';
    
    var isExpanded = stateExpandedSteps[0][step.key] || false;
    var hasDebugData = (step.debug_entries && step.debug_entries.length > 0) || (step.logs && step.logs.length > 0) || step.debug_data;
    
    return React.createElement('div', { key: step.key, className: 'border rounded-lg ' + borderClass },
      // Main step header
      React.createElement('div', { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center justify-between' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement('div', { className: 'p-2 rounded-full bg-slate-700' },
              React.createElement(Icon, { className: 'h-4 w-4 text-slate-300' })
            ),
            React.createElement('div', null,
              React.createElement('p', { className: 'font-medium text-slate-100' }, step.name),
              React.createElement('p', { className: 'text-xs text-slate-400' }, step.description)
            )
          ),
          React.createElement('div', { className: 'flex items-center gap-2' },
            renderStatusBadge(step.status),
            // Skip button
            canSkip && React.createElement(Button, {
              size: 'sm',
              variant: 'ghost',
              onClick: function() { handleSkip(step.key); },
              disabled: stateSkipping[0] === step.key,
              className: 'text-amber-400 hover:bg-amber-500/20 hover:text-amber-300',
              title: 'Schritt überspringen'
            }, stateSkipping[0] === step.key 
              ? React.createElement(Loader2, { className: 'h-3 w-3 animate-spin' })
              : React.createElement(SkipForward, { className: 'h-3 w-3' })
            ),
            // Retry button
            canRetry && React.createElement(Button, {
              size: 'sm',
              variant: 'outline',
              onClick: function() { handleRetry(step.key); },
              disabled: stateRetrying[0] === step.key,
              className: 'text-slate-200 border-slate-600 hover:bg-slate-700 hover:text-white'
            }, stateRetrying[0] === step.key 
              ? React.createElement(Loader2, { className: 'h-3 w-3 animate-spin mr-1' })
              : React.createElement(RefreshCw, { className: 'h-3 w-3 mr-1' }), 
              'Retry'
            )
          )
        ),
        // Error message
        step.error && React.createElement('div', { className: 'mt-3 p-2 rounded bg-red-500/10 border border-red-500/30' },
          React.createElement('p', { className: 'text-sm text-red-400 font-mono' }, step.error)
        )
      ),
      
      // Debug toggle button
      hasDebugData && React.createElement('div', { className: 'border-t border-slate-700' },
        React.createElement('button', { 
          className: 'w-full px-4 py-2 flex items-center gap-2 text-xs text-slate-400 hover:bg-slate-700/50 transition-colors',
          onClick: function() { toggleStepExpanded(step.key); }
        },
          isExpanded 
            ? React.createElement(ChevronDown, { className: 'h-3 w-3' })
            : React.createElement(ChevronRight, { className: 'h-3 w-3' }),
          React.createElement(Bug, { className: 'h-3 w-3' }),
          'Debug-Informationen anzeigen',
          step.logs && step.logs.length > 0 && React.createElement('span', { className: 'ml-auto bg-slate-700 px-1.5 py-0.5 rounded text-[10px] text-slate-300' }, step.logs.length + ' Logs'),
          step.debug_entries && step.debug_entries.length > 0 && React.createElement('span', { className: 'ml-1 bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded text-[10px]' }, step.debug_entries.length + ' API-Calls')
        ),
        
        // Expanded debug content
        isExpanded && React.createElement('div', { className: 'p-4 bg-muted dark:bg-slate-950 space-y-4 rounded-b-lg' },
          // Logs section
          step.logs && step.logs.length > 0 && React.createElement('div', null,
            React.createElement('p', { className: 'text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1' },
              React.createElement(FileText, { className: 'h-3 w-3' }),
              'Logs (' + step.logs.length + ')'
            ),
            React.createElement(ScrollArea, { className: 'h-32 rounded border border-border bg-background dark:bg-slate-900 p-2' },
              step.logs.map(function(log, idx) {
                return React.createElement('div', { key: idx, className: 'text-xs font-mono mb-1' },
                  React.createElement('span', { className: 'text-muted-foreground' }, log.time ? log.time.split('T')[1].split('.')[0] + ' ' : ''),
                  React.createElement('span', { className: 'text-foreground' }, log.message)
                );
              })
            )
          ),
          
          // Debug entries (API calls)
          step.debug_entries && step.debug_entries.length > 0 && step.debug_entries.map(function(entry, idx) {
            return React.createElement('div', { key: idx, className: 'space-y-2' },
              React.createElement('p', { className: 'text-xs font-medium text-muted-foreground flex items-center gap-1' },
                React.createElement(Code, { className: 'h-3 w-3' }),
                'API Call #' + (idx + 1),
                entry.timestamp && React.createElement('span', { className: 'text-muted-foreground ml-2' }, entry.timestamp.split('T')[1].split('.')[0])
              ),
              // Request
              entry.request && React.createElement('div', null,
                React.createElement('div', { className: 'flex items-center justify-between mb-1' },
                  React.createElement('span', { className: 'text-[10px] text-blue-400 uppercase font-bold' }, 'Request'),
                  React.createElement(Button, { 
                    variant: 'ghost', 
                    size: 'sm', 
                    className: 'h-5 px-1.5 text-[10px]',
                    onClick: function() { copyToClipboard(JSON.stringify(entry.request, null, 2)); }
                  },
                    React.createElement(Copy, { className: 'h-2.5 w-2.5 mr-1' }), 'Kopieren'
                  )
                ),
                React.createElement(ScrollArea, { className: 'h-24 rounded border border-border bg-background dark:bg-slate-900 p-2' },
                  React.createElement('pre', { className: 'text-[10px] text-foreground font-mono whitespace-pre-wrap' },
                    JSON.stringify(entry.request, null, 2)
                  )
                )
              ),
              // Response
              entry.response && React.createElement('div', null,
                React.createElement('div', { className: 'flex items-center justify-between mb-1' },
                  React.createElement('span', { className: 'text-[10px] text-emerald-400 uppercase font-bold' }, 'Response'),
                  React.createElement(Button, { 
                    variant: 'ghost', 
                    size: 'sm', 
                    className: 'h-5 px-1.5 text-[10px]',
                    onClick: function() { copyToClipboard(JSON.stringify(entry.response, null, 2)); }
                  },
                    React.createElement(Copy, { className: 'h-2.5 w-2.5 mr-1' }), 'Kopieren'
                  )
                ),
                React.createElement(ScrollArea, { className: 'h-32 rounded border border-border bg-background dark:bg-slate-900 p-2' },
                  React.createElement('pre', { className: 'text-[10px] text-foreground font-mono whitespace-pre-wrap' },
                    JSON.stringify(entry.response, null, 2)
                  )
                )
              )
            );
          }),
          
          // Legacy debug_data (single object)
          step.debug_data && !step.debug_entries && React.createElement('div', { className: 'space-y-2' },
            React.createElement('p', { className: 'text-xs font-medium text-muted-foreground flex items-center gap-1' },
              React.createElement(Code, { className: 'h-3 w-3' }),
              'Debug Data'
            ),
            step.debug_data.request && React.createElement('div', null,
              React.createElement('div', { className: 'flex items-center justify-between mb-1' },
                React.createElement('span', { className: 'text-[10px] text-blue-400 uppercase font-bold' }, 'Request'),
                React.createElement(Button, { 
                  variant: 'ghost', 
                  size: 'sm', 
                  className: 'h-5 px-1.5 text-[10px]',
                  onClick: function() { copyToClipboard(JSON.stringify(step.debug_data.request, null, 2)); }
                },
                  React.createElement(Copy, { className: 'h-2.5 w-2.5 mr-1' }), 'Kopieren'
                )
              ),
              React.createElement(ScrollArea, { className: 'h-24 rounded border border-border bg-background dark:bg-slate-900 p-2' },
                React.createElement('pre', { className: 'text-[10px] text-foreground font-mono whitespace-pre-wrap' },
                  JSON.stringify(step.debug_data.request, null, 2)
                )
              )
            ),
            step.debug_data.response && React.createElement('div', null,
              React.createElement('div', { className: 'flex items-center justify-between mb-1' },
                React.createElement('span', { className: 'text-[10px] text-emerald-400 uppercase font-bold' }, 'Response'),
                React.createElement(Button, { 
                  variant: 'ghost', 
                  size: 'sm', 
                  className: 'h-5 px-1.5 text-[10px]',
                  onClick: function() { copyToClipboard(JSON.stringify(step.debug_data.response, null, 2)); }
                },
                  React.createElement(Copy, { className: 'h-2.5 w-2.5 mr-1' }), 'Kopieren'
                )
              ),
              React.createElement(ScrollArea, { className: 'h-32 rounded border border-border bg-background dark:bg-slate-900 p-2' },
                React.createElement('pre', { className: 'text-[10px] text-foreground font-mono whitespace-pre-wrap' },
                  JSON.stringify(step.debug_data.response, null, 2)
                )
              )
            )
          )
        )
      )
    );
  }
  
  function renderStatusBadge(status) {
    if (status === 'success') return React.createElement(Badge, { className: 'bg-emerald-500/20 text-emerald-600' }, 'Erfolgreich');
    if (status === 'failed') return React.createElement(Badge, { className: 'bg-red-500/20 text-red-600' }, 'Fehlgeschlagen');
    if (status === 'running') return React.createElement(Badge, { className: 'bg-blue-500/20 text-blue-600' }, 'Läuft');
    if (status === 'pending') return React.createElement(Badge, { className: 'bg-slate-500/20 text-slate-700 dark:text-slate-400' }, 'Ausstehend');
    if (status === 'skipped') return React.createElement(Badge, { className: 'bg-amber-500/20 text-amber-600' }, 'Übersprungen');
    return React.createElement(Badge, { variant: 'outline' }, status || '-');
  }
  
  var hasFailures = false;
  var allDone = steps.length > 0;
  for (var i = 0; i < steps.length; i++) {
    if (steps[i].status === 'failed') hasFailures = true;
    if (steps[i].status !== 'success' && steps[i].status !== 'skipped') allDone = false;
  }
  
  // Get SCAPI ID from instance
  var scapiId = instance && (instance.scapi_instance_id || instance.scapi_uuid);
  
  return React.createElement('div', { className: 'space-y-6' },
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', { className: 'flex items-center gap-4' },
        React.createElement(Button, { variant: 'ghost', size: 'icon', onClick: function() { navigate('/admin/orders'); }, className: 'text-slate-300 hover:text-white hover:bg-slate-700' },
          React.createElement(ArrowLeft, { className: 'h-5 w-5' })
        ),
        React.createElement('div', null,
          React.createElement('h1', { className: 'text-2xl font-bold text-slate-100' }, 'Bestellung #' + (order.id ? order.id.slice(0,8) : '')),
          React.createElement('p', { className: 'text-muted-foreground' }, order.created_at ? new Date(order.created_at).toLocaleString('de-DE') : '-')
        )
      ),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, { variant: 'outline', onClick: fetchData, className: 'text-slate-200 border-slate-600 hover:bg-slate-700 hover:text-white' },
          React.createElement(RefreshCw, { className: 'h-4 w-4 mr-2' }), 'Aktualisieren'
        ),
        renderStatusBadge(order.status)
      )
    ),
    hasFailures && React.createElement('div', { className: 'p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3' },
      React.createElement(AlertTriangle, { className: 'h-5 w-5 text-red-600' }),
      React.createElement('p', { className: 'text-red-600' }, 'Einige Schritte sind fehlgeschlagen')
    ),
    allDone && React.createElement('div', { className: 'p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3' },
      React.createElement(CheckCircle2, { className: 'h-5 w-5 text-emerald-600' }),
      React.createElement('p', { className: 'text-emerald-600' }, 'Provisioning abgeschlossen')
    ),
    React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-3 gap-6' },
      React.createElement('div', { className: 'lg:col-span-2' },
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'flex items-center gap-2 text-slate-100' },
              React.createElement(Server, { className: 'h-5 w-5' }), 'Provisioning-Schritte'
            )
          ),
          React.createElement(CardContent, null,
            steps.length === 0
              ? React.createElement('div', { className: 'text-center py-8 text-muted-foreground' },
                  React.createElement(Clock, { className: 'h-12 w-12 mx-auto mb-3 opacity-50' }),
                  React.createElement('p', null, 'Keine Provisioning-Daten')
                )
              : React.createElement('div', { className: 'space-y-3' },
                  steps.sort(function(a, b) { return a.order - b.order; }).map(renderStep)
                )
          )
        )
      ),
      React.createElement('div', { className: 'space-y-4' },
        // SCAPI Info Card
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, { className: 'pb-2' },
            React.createElement(CardTitle, { className: 'text-base flex items-center gap-2 text-slate-100' },
              React.createElement(Database, { className: 'h-4 w-4' }), 'SCAPI Instance'
            )
          ),
          React.createElement(CardContent, { className: 'text-sm space-y-3 text-slate-300' },
            scapiId
              ? React.createElement('div', null,
                  React.createElement('div', { className: 'flex items-center justify-between mb-2' },
                    React.createElement('span', { className: 'text-slate-400' }, 'Instance ID'),
                    React.createElement(Button, { variant: 'ghost', size: 'sm', className: 'h-6 px-2 text-slate-300 hover:text-white hover:bg-slate-700', onClick: function() { copyToClipboard(scapiId); } },
                      React.createElement(Copy, { className: 'h-3 w-3' })
                    )
                  ),
                  React.createElement('code', { className: 'text-xs bg-slate-700 text-slate-200 px-2 py-1 rounded block break-all' }, scapiId),
                  React.createElement(Dialog, { open: stateScapiOpen[0], onOpenChange: stateScapiOpen[1] },
                    React.createElement(DialogTrigger, { asChild: true },
                      React.createElement(Button, { variant: 'outline', size: 'sm', className: 'w-full mt-3 text-slate-200 border-slate-600 hover:bg-slate-700 hover:text-white' },
                        React.createElement(ExternalLink, { className: 'h-4 w-4 mr-2' }), 'Live Status abrufen'
                      )
                    ),
                    React.createElement(DialogContent, { className: 'max-w-3xl max-h-[80vh] bg-slate-800 border-slate-700' },
                      React.createElement(DialogHeader, null,
                        React.createElement(DialogTitle, { className: 'flex items-center gap-2 text-slate-100' },
                          React.createElement(Database, { className: 'h-5 w-5' }), 'SCAPI Live Status'
                        )
                      ),
                      React.createElement('div', { className: 'space-y-4' },
                        React.createElement('div', { className: 'flex items-center justify-between' },
                          React.createElement('code', { className: 'text-xs bg-slate-700 text-slate-200 px-2 py-1 rounded' }, scapiId),
                          React.createElement(Button, { variant: 'outline', size: 'sm', onClick: fetchScapiStatus, disabled: stateScapiLoading[0], className: 'text-slate-200 border-slate-600 hover:bg-slate-700 hover:text-white' },
                            stateScapiLoading[0] 
                              ? React.createElement(Loader2, { className: 'h-4 w-4 animate-spin mr-2' })
                              : React.createElement(RefreshCw, { className: 'h-4 w-4 mr-2' }),
                            'Neu laden'
                          )
                        ),
                        stateScapiLoading[0]
                          ? React.createElement('div', { className: 'flex justify-center py-12' },
                              React.createElement(Loader2, { className: 'h-8 w-8 animate-spin text-blue-400' })
                            )
                          : stateScapiData[0] && stateScapiData[0].error
                            ? React.createElement('div', { className: 'p-4 rounded bg-red-500/10 border border-red-500/30' },
                                React.createElement('p', { className: 'text-red-400' }, stateScapiData[0].error)
                              )
                            : stateScapiData[0]
                              ? React.createElement(ScrollArea, { className: 'h-[50vh] rounded border border-slate-600 bg-slate-900 p-4' },
                                  React.createElement('pre', { className: 'text-xs text-slate-200 font-mono whitespace-pre-wrap' },
                                    JSON.stringify(stateScapiData[0].raw_json, null, 2)
                                  )
                                )
                              : React.createElement('p', { className: 'text-slate-400 text-center py-8' }, 'Klicke auf "Neu laden" um den Status abzurufen')
                      )
                    )
                  )
                )
              : React.createElement('p', { className: 'text-slate-400 text-center py-4' }, 'Keine SCAPI ID vorhanden')
          )
        ),
        // Customer Card
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, { className: 'pb-2' },
            React.createElement(CardTitle, { className: 'text-base flex items-center gap-2 text-slate-100' },
              React.createElement(User, { className: 'h-4 w-4' }), 'Kunde'
            )
          ),
          React.createElement(CardContent, { className: 'text-sm text-slate-300' },
            customer
              ? React.createElement('div', null,
                  React.createElement('p', { className: 'font-medium text-slate-100' }, (customer.first_name || '') + ' ' + (customer.last_name || '')),
                  React.createElement('p', { className: 'text-slate-400' }, customer.email || '')
                )
              : React.createElement('p', { className: 'text-slate-400' }, '-')
          )
        ),
        // Plan Card
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, { className: 'pb-2' },
            React.createElement(CardTitle, { className: 'text-base flex items-center gap-2 text-slate-100' },
              React.createElement(HardDrive, { className: 'h-4 w-4' }), 'Plan'
            )
          ),
          React.createElement(CardContent, { className: 'text-sm space-y-1 text-slate-300' },
            React.createElement('div', { className: 'flex justify-between' },
              React.createElement('span', { className: 'text-slate-400' }, 'Speicher'),
              React.createElement('span', { className: 'text-slate-200' }, (plan.storage_gb || 250) + ' GB')
            ),
            React.createElement('div', { className: 'flex justify-between' },
              React.createElement('span', { className: 'text-slate-400' }, 'Domain'),
              React.createElement('span', { className: 'text-slate-200' }, plan.custom_domain || plan.cloud_url || '-')
            )
          )
        ),
        // Instance Card
        instance && React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, { className: 'pb-2' },
            React.createElement(CardTitle, { className: 'text-base flex items-center gap-2 text-slate-100' },
              React.createElement(Server, { className: 'h-4 w-4' }), 'Instanz'
            )
          ),
          React.createElement(CardContent, { className: 'text-sm space-y-1 text-slate-300' },
            React.createElement('div', { className: 'flex justify-between' },
              React.createElement('span', { className: 'text-slate-400' }, 'Status'),
              renderStatusBadge(instance.status)
            ),
            instance.url && React.createElement('a', { href: instance.url, target: '_blank', rel: 'noopener noreferrer', className: 'text-blue-400 text-xs hover:underline block mt-2' }, instance.url)
          )
        ),
        // Payment Card
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, { className: 'pb-2' },
            React.createElement(CardTitle, { className: 'text-base flex items-center gap-2 text-slate-100' },
              React.createElement(CreditCard, { className: 'h-4 w-4' }), 'Zahlung'
            )
          ),
          React.createElement(CardContent, { className: 'text-sm space-y-1 text-slate-300' },
            React.createElement('div', { className: 'flex justify-between' },
              React.createElement('span', { className: 'text-slate-400' }, 'Betrag'),
              React.createElement('span', { className: 'text-slate-200' }, (order.total ? order.total.toFixed(2) : '0.00') + ' €')
            ),
            React.createElement('div', { className: 'flex justify-between' },
              React.createElement('span', { className: 'text-slate-400' }, 'Methode'),
              React.createElement('span', { className: 'text-slate-200' }, order.payment_method || '-')
            )
          )
        )
      )
    )
  );
}

export default AdminOrderDetail;
